type QueryParamPrimitive = string | number | boolean;
type QueryParamValue = QueryParamPrimitive | null | undefined;

export type ApiQueryParams = Record<string, QueryParamValue | QueryParamValue[]>;

export interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  baseURL?: string;
  params?: ApiQueryParams;
  body?: BodyInit | Record<string, unknown> | unknown[] | null;
  json?: unknown;
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 30_000;
const DEFAULT_FALLBACK_API_URL = "https://eportifolio-api-dev.onicode.com.br";
let hasWarnedMissingApiUrl = false;

const isAbsoluteUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const getConfiguredBaseUrl = (): string => {
  const raw = process.env.NEXT_PUBLIC_API_URL ?? "";
  const normalized = raw.trim().replace(/\/+$/, "");

  if (normalized) return normalized;

  if (!hasWarnedMissingApiUrl) {
    hasWarnedMissingApiUrl = true;
    console.warn("apiFetch: NEXT_PUBLIC_API_URL nao definido. Usando fallback padrao.");
  }

  return DEFAULT_FALLBACK_API_URL;
};

const appendParams = (url: URL, params?: ApiQueryParams): void => {
  if (!params) return;

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === undefined || item === null) return;
        url.searchParams.append(key, String(item));
      });
      continue;
    }

    url.searchParams.append(key, String(value));
  }
};

const buildRelativeUrlWithParams = (path: string, params?: ApiQueryParams): string => {
  if (!params) return path;

  const [pathWithoutHash, hash = ""] = path.split("#");
  const [pathname, query = ""] = pathWithoutHash.split("?");
  const searchParams = new URLSearchParams(query);

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === undefined || item === null) return;
        searchParams.append(key, String(item));
      });
      continue;
    }

    searchParams.append(key, String(value));
  }

  const qs = searchParams.toString();
  return `${pathname}${qs ? `?${qs}` : ""}${hash ? `#${hash}` : ""}`;
};

const buildRequestUrl = (path: string, baseURL: string, params?: ApiQueryParams): string => {
  if (isAbsoluteUrl(path)) {
    const absolute = new URL(path);
    appendParams(absolute, params);
    return absolute.toString();
  }

  if (!baseURL) {
    return buildRelativeUrlWithParams(path, params);
  }

  if (!isAbsoluteUrl(baseURL)) {
    const normalizedBase = (baseURL.startsWith("/") ? baseURL : `/${baseURL}`).replace(/\/+$/, "");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return buildRelativeUrlWithParams(`${normalizedBase}${normalizedPath}`, params);
  }

  const target = new URL(path.startsWith("/") ? path : `/${path}`, `${baseURL}/`);
  appendParams(target, params);
  return target.toString();
};

const isBodySerializableObject = (value: unknown): value is Record<string, unknown> | unknown[] => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return true;
  if (value instanceof FormData) return false;
  if (value instanceof URLSearchParams) return false;
  if (value instanceof Blob) return false;
  if (value instanceof ArrayBuffer) return false;
  return typeof value === "object";
};

export async function apiFetch(path: string, options: ApiFetchOptions = {}): Promise<Response> {
  const { baseURL, params, timeoutMs = DEFAULT_TIMEOUT_MS, json, ...requestInit } = options;
  const headers = new Headers(requestInit.headers);

  let body = requestInit.body;

  if (json !== undefined) {
    body = JSON.stringify(json);
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
  } else if (isBodySerializableObject(body)) {
    body = JSON.stringify(body);
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
  }

  if (body instanceof FormData) {
    // Let the runtime set multipart/form-data boundary automatically.
    headers.delete("Content-Type");
  }

  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }

  const url = buildRequestUrl(path, baseURL ?? getConfiguredBaseUrl(), params);

  const controller = new AbortController();
  const externalSignal = requestInit.signal;

  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort(externalSignal.reason);
    } else {
      externalSignal.addEventListener("abort", () => controller.abort(externalSignal.reason), { once: true });
    }
  }

  const timeoutId =
    timeoutMs > 0
      ? setTimeout(() => {
          controller.abort(new Error(`Request timeout after ${timeoutMs}ms`));
        }, timeoutMs)
      : undefined;

  try {
    return await fetch(url, {
      ...requestInit,
      headers,
      body: body as BodyInit | null | undefined,
      signal: controller.signal,
    });
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}
